import { useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { useToBook } from '@/hooks/useSchedule';

/**
 * Jobs is one sidebar item with three ways of looking at the same work: the list, the
 * schedule board and the map. Views never get their own sidebar item.
 */
export type JobsView = 'list' | 'schedule' | 'map';

export const JOBS_VIEW_PATH: Record<JobsView, string> = {
  list: '/jobs',
  schedule: '/jobs/schedule',
  map: '/jobs/map',
};

const STORAGE_KEY = 'steddy.jobs.view';
/** Kept in the user's auth metadata so the choice follows them between computers. */
const METADATA_KEY = 'jobs_view';

const isJobsView = (v: unknown): v is JobsView => v === 'list' || v === 'schedule' || v === 'map';

function readLocal(): JobsView | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isJobsView(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Which views this person gets. Map is office-only. It shows even with technician tracking off:
 * the day's jobs are still on it, and the page itself says how to turn tracking on.
 */
export function useJobsViews() {
  const { isAdmin, isCSR } = useUserRole();
  const staff = isAdmin || isCSR;
  const { data: toBook } = useToBook({ enabled: staff });

  const views: JobsView[] = ['list', 'schedule', ...(staff ? (['map'] as const) : [])];
  const toBookCount = staff ? toBook.jobs.length + toBook.leads.length : 0;
  return { views, staff, toBookCount };
}

/** Where the sidebar's Jobs item should land: the view this person used last. */
export function useJobsHomePath(views: JobsView[]): string {
  const { user } = useAuth();
  const saved = user?.user_metadata?.[METADATA_KEY];
  const last = isJobsView(saved) ? saved : readLocal();
  return JOBS_VIEW_PATH[last && views.includes(last) ? last : 'list'];
}

/** Call from each Jobs view: records it as this person's last view. */
export function useRememberJobsView(view: JobsView) {
  const { user } = useAuth();
  const saved = user?.user_metadata?.[METADATA_KEY];

  const remember = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, view);
    } catch {
      /* private window: the profile copy still works */
    }
    // Only write when it changed: every write re-issues the session.
    if (user && saved !== view) {
      void supabase.auth.updateUser({ data: { [METADATA_KEY]: view } });
    }
  }, [user, saved, view]);

  useEffect(() => {
    remember();
  }, [remember]);
}
